/**
 * StockAdjustForm — modal body for adjusting a product's on-hand stock.
 * Writes a StockMovement audit record (type=ADJUSTMENT) and updates Product.currentStock.
 */
import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useMemo, useState } from 'react';
import { Product } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { adjustStock } from '@/inventory/InventoryCrudService';

const QUICK_DELTAS = [-10, -5, -1, +1, +5, +10];
const REASON_PRESETS = ['Recount', 'Damaged', 'Expired', 'Supplier shortage', 'Theft / loss'];

export function StockAdjustForm({
  product,
  onDone,
  onCancel,
}: {
  product: Product;
  onDone: () => void;
  onCancel: () => void;
}) {
  const userId = useAppStore((s) => s.currentUser?.id);
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [saving, setSaving] = useState(false);

  const parsed = parseFloat(delta);
  const isValid = Number.isFinite(parsed) && parsed !== 0;
  const changeMode: 'add' | 'remove' | null = !isValid ? null : parsed > 0 ? 'add' : 'remove';
  const rawAfter = product.currentStock + (isValid ? parsed : 0);
  const newStock = Math.max(0, rawAfter);
  const willClamp = isValid && rawAfter < 0;
  const canSubmit = isValid && reason.trim().length > 0;

  const groupedDeltas = useMemo(
    () => ({
      remove: QUICK_DELTAS.filter((d) => d < 0),
      add: QUICK_DELTAS.filter((d) => d > 0),
    }),
    [],
  );

  function bump(amount: number) {
    const cur = parseFloat(delta);
    const base = Number.isFinite(cur) ? cur : 0;
    const next = Math.round((base + amount) * 100) / 100;
    setDelta(next === 0 ? '' : String(next));
  }

  function setMode(mode: 'add' | 'remove') {
    const cur = parseFloat(delta);
    const abs = Number.isFinite(cur) ? Math.abs(cur) : 1;
    setDelta(String(mode === 'add' ? abs : -abs));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) { setError('Enter a non-zero quantity (positive to add, negative to remove).'); return; }
    if (!reason.trim()) { setError('Please describe the reason for this adjustment.'); return; }
    setError(null);
    setSaving(true);
    try {
      await adjustStock(product, parsed, reason.trim(), { userId: userId ?? null });
      notifySuccess(`${product.name}: stock ${parsed > 0 ? 'increased' : 'reduced'} by ${Math.abs(parsed)}.`);
      onDone();
    } catch (err: any) {
      setError(err?.message || 'Failed to adjust stock.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex max-h-[min(82vh,620px)] flex-col">
      <div className="space-y-3 overflow-y-auto px-4 py-3">
        <div className="rounded-md border border-stroke-hairline/10 bg-bg-canvas p-2.5">
          <div className="text-label uppercase text-fg-tertiary">Product</div>
          <div className="mt-0.5 text-body text-fg-primary">{product.name}</div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-label uppercase text-fg-tertiary">Current</span>
            <span className="text-heading tabular-nums text-fg-primary">{product.currentStock}</span>
            <span className="text-body text-fg-secondary">{product.unit}</span>
            <span className="ml-auto text-label uppercase text-fg-tertiary">After</span>
            <span
              className={cn(
                'text-heading tabular-nums',
                !isValid ? 'text-fg-primary' : parsed > 0 ? 'text-success-ink' : 'text-danger-ink',
              )}
            >
              {newStock}
            </span>
          </div>
        </div>

        <div className="rounded-md border border-stroke-hairline/10 bg-bg-canvas p-2.5">
          <div className="mb-2 text-label uppercase text-fg-tertiary">Direction</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('remove')}
              className={cn(
                'rounded-md border px-2.5 py-1 text-label transition-colors',
                changeMode === 'remove'
                  ? 'border-danger/30 bg-danger/10 text-danger-ink'
                  : 'border-stroke-hairline/15 text-fg-secondary hover:bg-bg-subtle',
              )}
            >
              Remove stock
            </button>
            <button
              type="button"
              onClick={() => setMode('add')}
              className={cn(
                'rounded-md border px-2.5 py-1 text-label transition-colors',
                changeMode === 'add'
                  ? 'border-success/30 bg-success/10 text-success-ink'
                  : 'border-stroke-hairline/15 text-fg-secondary hover:bg-bg-subtle',
              )}
            >
              Add stock
            </button>
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-label uppercase text-fg-tertiary">Change (+ / −)</span>
          <Input
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="e.g. 5 to add, -2 to remove"
            inputMode="decimal"
            autoFocus
          />
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {groupedDeltas.remove.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => bump(d)}
                  className="rounded-md border border-stroke-hairline/15 px-2 py-0.5 text-label tabular-nums text-danger-ink transition-colors duration-120 ease-apple hover:bg-bg-subtle"
                >
                  {d}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {groupedDeltas.add.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => bump(d)}
                  className="rounded-md border border-stroke-hairline/15 px-2 py-0.5 text-label tabular-nums text-success-ink transition-colors duration-120 ease-apple hover:bg-bg-subtle"
                >
                  +{d}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 text-label text-fg-tertiary">
            Use + to add stock and - to remove. Final stock never goes below 0.
          </div>
        </label>

        {isValid && (
          <div className="rounded-md border border-stroke-hairline/10 bg-bg-canvas px-3 py-2 text-body">
            <span className="text-fg-secondary">This change will </span>
            <span className={parsed > 0 ? 'text-success-ink' : 'text-danger-ink'}>
              {parsed > 0 ? 'increase' : 'decrease'} stock by {Math.abs(parsed)} {product.unit}
            </span>
            <span className="text-fg-secondary">.</span>
          </div>
        )}

        {willClamp && (
          <div className="rounded-md border border-warning/20 bg-warning/10 px-3 py-2 text-body text-warning-ink">
            Removing more than the {product.currentStock} {product.unit} on hand — stock will be set to 0.
          </div>
        )}

        <label className="block">
          <span className="mb-1 block text-label uppercase text-fg-tertiary">Reason</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Damaged packet found during recount"
            rows={2}
            className="w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 py-2 text-body text-fg-primary placeholder:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {REASON_PRESETS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                className={cn(
                  'rounded-md border px-2 py-0.5 text-label transition-colors duration-120 ease-apple',
                  reason === r
                    ? 'border-accent/30 bg-accent/10 text-accent-ink'
                    : 'border-stroke-hairline/15 text-fg-secondary hover:bg-bg-subtle',
                )}
              >
                {r}
              </button>
            ))}
          </div>
        </label>

      </div>

      <div className="flex items-center justify-end gap-2 border-t border-stroke-hairline/10 px-4 py-2.5">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving || !canSubmit}>{saving ? 'Saving…' : 'Adjust stock'}</Button>
      </div>
    </form>
  );
}
